import { Button, Card, Center, Loader } from '@mantine/core';
import { useModals } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { IconArrowBackUp, IconEdit, IconTrash } from '@tabler/icons-react';
import PageContent from '../../../../components/core/PageContent/PageContent';
import PageHeader from '../../../../components/core/PageHeader/PageHeader';
import { DepartmentType } from '../../../../models/core/departments.type';
import departmentsService from '../../../../services/core/departments.service';
import { Feature } from '../../../../utils/constants.utils';
import DataView from './components/DataView';

type Result = {
  loading: boolean;
  data: DepartmentType | null;
};

function DepartmentView() {
  const navigate = useNavigate();
  const location: any = useLocation();
  const modals = useModals();
  const { idDepartamento } = useParams();

  const [result, setResult] = useState<Result>({
    loading: location?.state?.department === undefined,
    data: null,
  });
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      let data: DepartmentType | null = null;
      try {
        data = await departmentsService.select({ idDepartamento: Number(idDepartamento) });
      } catch (error: any) {
        showNotification({
          title: 'Departamento - Visualizar',
          message: error?.isBusinessException
            ? error.description
            : `Não foi possível carregar o departamento.`,
          color: 'red',
        });
        navigate(`/${error?.statusCode || '500'}`, {
          replace: true,
        });
      } finally {
        setResult({ loading: false, data });
      }
    };
    if (result.data === null) {
      fetchData();
    }
  }, [idDepartamento, navigate, result]);

  const handleAction = async () => {
    try {
      setResult({ loading: false, data: result.data });

      setDeleting(true);
      await departmentsService.delete({ idDepartamento: Number(idDepartamento) });
      navigate('/departments');

      showNotification({
        title: 'Departamento - Visualizar',
        message: `Departamento excluído com sucesso.`,
        color: 'green',
      });
    } catch (error: any) {
      showNotification({
        title: 'Departamento - Visualizar',
        message: error?.isBusinessException ? error.description : `Não foi possível excluir o departamento.`,
        color: 'red',
      });
    } finally {
      setDeleting(false);
    }
  };

  const showModal = () =>
    modals.openConfirmModal({
      title: `Gostaria de excluir esse departamento?`,
      size: 'sm',
      closeOnClickOutside: false,
      closeOnEscape: false,
      onConfirm: () => {
        handleAction();
      },
    });

  return (
    <Card>
      <PageHeader
        feature={Feature.Managerial.Department}
        title="Departamento - Visualizar"
        description="Visualize os detalhes de um departamento."
        buttons={[
          <Button
            key="Voltar"
            color="secondary"
            leftSection={<IconArrowBackUp size={18} />}
            onClick={() => {
              navigate(-1);
            }}
            disabled={deleting || result.loading}
          >
            Voltar
          </Button>,
          <Button
            key="Excluir"
            color="red"
            leftSection={<IconTrash size={18} />}
            onClick={() => {
              showModal();
            }}
            disabled={deleting || result.loading}
            hidden={result.data?.executivo}
            loading={deleting}
          >
            Excluir
          </Button>,
          <Button
            key="Editar"
            color="accent"
            leftSection={<IconEdit size={18} />}
            onClick={() => {
              navigate(`/departments/${result?.data?.idDepartamento}/edit`, {
                state: {
                  department: result.data,
                },
              });
            }}
            hidden={result.data?.executivo}
            disabled={deleting || result.loading}
          >
            Editar
          </Button>,
        ]}
      />
      {result.data === null ? (
        <Center>
          <Loader size="xl" />
        </Center>
      ) : (
        <PageContent>
          <DataView data={result.data} />
        </PageContent>
      )}
    </Card>
  );
}

export default DepartmentView;
